import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useMutation, useQuery } from 'urql';
import DestructiveAction, {
  DestructiveActionDescription,
  DestructiveActionHeading,
} from '@/components/Custom/DestructiveAction';
import ContentBoundary from '@/components/UI/ContentBoundary';
import EmptyPlaceholder from '@/components/UI/EmptyPlaceholder';
import { graphql } from '@/gql/graphql';
import useRootStore from '@/stores/useRootStore';

const GetOrganizationDelete = graphql(`
  query GetOrganizationDelete($organizationId: uuid!) {
    event_by_pk(id: $organizationId) {
      id
      title
      event_type
    }
  }
`);
const DeleteOrganization = graphql(`
  mutation DeleteOrganization($organizationId: uuid!) {
    delete_event_by_pk(id: $organizationId) {
      id
    }
  }
`);

export default function OrganizationDelete() {
  const { organizationId } = useLocalSearchParams<{ organizationId: string }>();
  const { dismissTo } = useRouter();
  const [, remove] = useMutation(DeleteOrganization);
  const [result, refresh] = useQuery({
    query: GetOrganizationDelete,
    variables: { organizationId },
  });
  const organization = result.data?.event_by_pk;
  const removeOrganization = async () => {
    const result = await remove({ organizationId });
    return { success: !result.error && !!result.data?.delete_event_by_pk };
  };
  return (
    <>
      <Stack.Screen
        options={{
          title: organization?.title
            ? `Delete ${organization.title}`
            : 'Delete organization',
        }}
      />
      <ContentBoundary
        data={
          organization?.event_type === 'organization' && organization.title
            ? { id: organization.id, title: organization.title }
            : null
        }
        loading={result.fetching}
        error={result.error}
        onRetry={() => refresh({ requestPolicy: 'network-only' })}
        empty={<EmptyPlaceholder message="Deletion is unavailable." />}
      >
        {(organization) => (
          <DestructiveAction
            key={organization.id}
            confirmationName={organization.title}
            label="Delete organization"
            submittingLabel="Deleting…"
            onAction={removeOrganization}
            onSuccess={() => {
              useRootStore.setState({
                organizationId: null,
                activeContextPathname: null,
                activeTab: 'event',
              });
              dismissTo('/event');
            }}
          >
            <DestructiveActionHeading>
              Are you sure you want to delete {organization.title}?
            </DestructiveActionHeading>
            <DestructiveActionDescription>
              This cannot be undone.
            </DestructiveActionDescription>
          </DestructiveAction>
        )}
      </ContentBoundary>
    </>
  );
}
